import PayWall from './PayWall';
import Calls from './Calls';
import { useState, useEffect } from 'react';

function App() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('deadline');
  const callsPerPage = 20;
  const [activeFilters, setActiveFilters] = useState([]);

  useEffect(() => {
    fetch('/calls.json')
      .then(response => response.json())
      .then(data => setCalls(data))
      .finally(() => setLoading(false));
  }, []);

  const sortCalls = (calls) => {
    return [...calls].sort((a, b) => {
      switch (sortOrder) {
        case 'deadline':
          return new Date(a.entry_deadline) - new Date(b.entry_deadline);
        case 'price':
          return a.entry_fee_order_index - b.entry_fee_order_index;
        case 'newest':
          return a.created_at_order_index - b.created_at_order_index;
        default:
          return 0;
      }
    });
  };

  const freeCalls = calls.filter(call => call.entry_fee === 0).length;
  const callTypes = calls.reduce((acc, call) => {
    if (call.call_type?.name) {
      acc[call.call_type.name] = (acc[call.call_type.name] || 0) + 1;
    }
    return acc;
  }, {});

  const toggleFilter = (filterType) => {
    setActiveFilters(prev => {
      const isActive = prev.includes(filterType);
      if (isActive) {
        return prev.filter(f => f !== filterType);
      } else {
        return [...prev, filterType];
      }
    });
    setCurrentPage(1);
  };

  const filteredCalls = sortCalls(calls.filter(call => {
    const searchRegex = new RegExp(searchText, 'i');
    const matchesSearch = searchText === '' || 
                         searchRegex.test(call.name) || 
                         searchRegex.test(call.description);
    
    // Separate free filter from call type filters
    const callTypeFilters = activeFilters.filter(f => f !== 'free');
    const hasFreeFilter = activeFilters.includes('free');
    
    // Check if matches free filter (if active)
    const matchesFreeFilter = !hasFreeFilter || call.entry_fee === 0;
    
    // Check if matches any call type filter (OR logic)
    const matchesCallTypes = callTypeFilters.length === 0 || 
                            callTypeFilters.some(filter => call.call_type?.name === filter);

    return matchesSearch && matchesFreeFilter && matchesCallTypes;
  }));

  const indexOfLastCall = currentPage * callsPerPage;
  const indexOfFirstCall = indexOfLastCall - callsPerPage;
  const currentCalls = filteredCalls.slice(indexOfFirstCall, indexOfLastCall);
  const totalPages = Math.ceil(filteredCalls.length / callsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const resetFilters = () => {
    setSearchText('');
    setSortOrder('deadline');
    setCurrentPage(1);
    setActiveFilters([]);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen">
      <header className="bg-blue-500 p-4 shadow-lg">
        <div className="container mx-auto">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl md:text-3xl font-bold text-white">EntryHaven</h1>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <button
              onClick={() => toggleFilter('free')}
              className={`px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors
                ${activeFilters.includes('free') 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'bg-white text-purple-600 hover:bg-gray-100'}`}
            >
              {freeCalls} free calls
            </button>
            {Object.entries(callTypes).map(([type, count]) => (
              <button
                key={type}
                onClick={() => toggleFilter(type)}
                className={`px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors
                  ${activeFilters.includes(type)
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-white text-purple-600 hover:bg-gray-100'}`}
              >
                {count} {type}
              </button>
            ))}
            {activeFilters.length > 0 && (
              <button
                onClick={resetFilters}
                className="text-sm px-3 py-1 bg-transparent hover:bg-blue-400 text-white rounded-md cursor-pointer transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="mt-4">
            <input
              type="text"
              placeholder="Search calls..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full p-2 rounded-lg bg-white"
            />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4">
        {calls.length > 0 ? (
          <>
            {filteredCalls.length > 0 ? (
              <>
                <div className="flex justify-between items-center py-4 text-gray-600">
                  <div>
                    Showing {indexOfFirstCall + 1}-{Math.min(indexOfLastCall, filteredCalls.length)} of {filteredCalls.length} calls
                  </div>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="px-4 py-2 rounded-lg border border-gray-200 cursor-pointer"
                  >
                    <option value="deadline">Deadline (soonest)</option>
                    <option value="price">Price (cheapest)</option>
                    <option value="newest">Newest</option>
                  </select>
                </div>
                <Calls calls={currentCalls} searchText={searchText} />
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 my-8">
                    <button 
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded bg-gray-200 disabled:opacity-50 cursor-pointer hover:bg-gray-300"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button 
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded bg-gray-200 disabled:opacity-50 cursor-pointer hover:bg-gray-300"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-600">
                No calls currently match your search criteria. Try adjusting your search terms.
              </div>
            )}
          </>
        ) : (
          <PayWall />
        )}
      </main>
    </div>
  );
}

export default App;
